import DocumentEditorPanel from "Areas/Documents/documentEditorPanel";
import ProjectEditorPanel from "Areas/Projects/projectEditorPanel";
import SummaryReportEditorPanel from "Areas/SummaryReports/summaryReportEditorPanel";
import workSpacesKeys from "Areas/WorkSpaces/routingKeys";
import WorkSpaceEditorPanel from "Areas/WorkSpaces/workSpaceEditorPanel";
import { SORT_ORDER } from "Common/dictionaries/sortOrder";
import { routesAndPagesMap } from "Common/routingUtils";
import MENU_LOGO from "Content/images/logo/HunterLogoFull.svg";
import classNames from "classnames";
import compact from "lodash/compact";
import find from "lodash/find";
import flatMap from "lodash/flatMap";
import head from "lodash/head";
import map from "lodash/map";
import omit from "lodash/omit";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NAV_CENTERED_CONTENT_CLASS_NAME, NAV_MB_DEFAULT_GAP_CLASS_NAME, SidebarMenuItem, SidebarMenuList, SidebarSearchPanel, Styled } from "../Common";
import locale from "./workSpacesSidebarMenu.locale";
import WorkSpacesSidebarMenuStyled from "./workSpacesSidebarMenu.styled";
import styles from "./workSpacesSidebarMenu.module.css";
const buildWorkSpaceKey = (w) => (w ? `workspace_${w.Id}` : undefined);
const buildProjectKey = (p) => (p ? `project_${p.Id}` : "");
const availableSortBy = [
    { Key: "sourceData.Created", Name: locale.SORT_BY_CREATION },
    { Key: "title", Name: locale.SORT_BY_TITLE }
];
export default function WorkSpacesSidebarMenu(props) {
    var _a;
    const [selected, setSelected] = useState(undefined);
    const selectedSet = useMemo(() => new Set(selected ? [selected] : []), [selected]);
    const [filterText, setFilterText] = useState("");
    const [sortBy, setSortBy] = useState((_a = head(availableSortBy)) === null || _a === void 0 ? void 0 : _a.Key);
    const [sortOrder, setSortOrder] = useState(SORT_ORDER.DESC);
    const addBtn = useMemo(() => {
        if (!props.canEditWorkSpaces)
            return undefined;
        const btn = new SidebarMenuItem("create-work-space", locale.CREATE_WORK_SPACE, "icon-add font-h3 text-primary-new");
        btn.className = classNames(NAV_MB_DEFAULT_GAP_CLASS_NAME, NAV_CENTERED_CONTENT_CLASS_NAME, styles["add-btn"], styles["menu-item"]);
        return btn;
    }, []);
    useEffect(() => {
        props.canGetWorkSpaces && props.onGetWorkSpaces && props.onGetWorkSpaces();
    }, []);
    const handleItemClick = useCallback((item) => {
        if (addBtn && item.key === addBtn.key) {
            props.onOpenWorkSpaceCreatorPanel && props.onOpenWorkSpaceCreatorPanel();
        }
    }, [props.onOpenWorkSpaceCreatorPanel]);
    const sidebarMenuItems = useMemo(() => compact(map(props.workSpaces, w => {
        const key = buildWorkSpaceKey(w);
        if (!key)
            return undefined;
        const e = new SidebarMenuItem(key, w.Name, undefined, undefined, omit(w, "Projects"), map(w.Projects, p => {
            const wp = new SidebarMenuItem(buildProjectKey(p), p.Name, undefined, undefined, p);
            wp.className = classNames(styles["menu-item"]);
            wp.useDotIcon = false;
            wp.hash = props.getProjectDetailsHref(p.Id);
            return wp;
        }));
        e.className = classNames(styles["menu-item"]);
        e.hash = routesAndPagesMap.getPath(workSpacesKeys.WORK_SPACE, w.Id);
        return e;
    })), [props.workSpaces]);
    const flatSidebarMenuItems = useMemo(() => flatMap(sidebarMenuItems, e => (e.subItems ? [e, ...e.subItems] : [e])), [sidebarMenuItems]);
    useEffect(() => {
        if (!flatSidebarMenuItems.length || !props.locationHash)
            return;
        const newSelected = find(flatSidebarMenuItems, ["hash", props.locationHash]);
        setSelected(newSelected === null || newSelected === void 0 ? void 0 : newSelected.key);
    }, [flatSidebarMenuItems, props.locationHash]);
    const handleToggleSortOrder = useCallback(() => {
        setSortOrder(prev => (prev === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC));
    }, []);
    const handleChangeSortBy = useCallback((value) => {
        setSortBy(value);
    }, []);
    const logoImg = useMemo(() => React.createElement("img", { draggable: "false", alt: props.productName, src: MENU_LOGO }), [props.productName]);
    const logoAndSearchPanel = useMemo(() => (React.createElement(Styled.LogoContainer, { sidebarWidth: props.width || 0 },
        props.defaultHash ? React.createElement("a", { href: props.defaultHash }, logoImg) : logoImg,
        React.createElement(SidebarSearchPanel, { filterText: filterText, onFilterTextChange: setFilterText, availableSortBy: availableSortBy, sortBy: sortBy, sortOrder: sortOrder, onToggleSortOrder: handleToggleSortOrder, onChangeSortBy: handleChangeSortBy }))), [
        logoImg,
        props.defaultHash,
        props.width,
        filterText,
        sortBy,
        sortOrder,
        handleToggleSortOrder,
        handleChangeSortBy
    ]);
    return (React.createElement(WorkSpacesSidebarMenuStyled.SidebarMenuContainer, { width: props.width, className: props.className, bgColorDefault: "navbar-bg" },
        logoAndSearchPanel,
        React.createElement(SidebarMenuList, { inProgress: props.inProgress, selectedItems: selectedSet, activeItems: selectedSet, sidebarMenuItems: sidebarMenuItems, sidebarMenuButton: addBtn, onItemClick: handleItemClick, filterText: filterText, sortBy: sortBy, sortOrder: sortOrder, bgColorActive: "menu-items-group-line" }),
        props.header && React.createElement(props.header, null),
        props.canEditWorkSpaces && React.createElement(WorkSpaceEditorPanel, null),
        props.canEditProjects && React.createElement(ProjectEditorPanel, null),
        props.canEditDocuments && React.createElement(DocumentEditorPanel, null),
        props.canEditSummaryReports && React.createElement(SummaryReportEditorPanel, null)));
}
WorkSpacesSidebarMenu.defaultProps = {
    isExpanded: true
};
